import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, HostBinding, Component, NgModule } from '@angular/core';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';

/**
 * To use this component in a module, you can import the `CentricAlertModule`:
 * @example
 * ```typescript
 * import { CentricAlertModule } from '@windmill/ng-windmill/alert';
 * ```
 *
 * To use the standalone component directly, you can import `AlertComponent`:
 * @example
 * ```typescript
 * import { AlertComponent } from '@windmill/ng-windmill/alert';
 * ```
 */
class AlertComponent {
    role = 'alert';
    /**
     * The type of the alert
     */
    type = 'info';
    /**
     * The custom icon for alert.
     */
    svgIcon;
    /**
     * The size of the icon.
     */
    size;
    /**
     * Data-testId attribute
     */
    testId;
    /**
     * Boolean value to show the close button.
     */
    hasCloseButton = false;
    /**
     * If true, the component will look like a banner
     */
    isBanner = false;
    /**
     * The aria label attribute for close button
     */
    ariaLabelCloseButton;
    /**
     * An event returned when the close button is clicked.
     */
    closed = new EventEmitter();
    /**
     * The list of icons name for each alert type
     */
    icons = {
        info: 'info-circle_b',
        warning: 'exclamation-circle_b',
        success: 'check-circle_b',
        error: 'exclamation-triangle_b'
    };
    closeButtonClick() {
        this.closed.emit();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: AlertComponent, isStandalone: true, selector: "centric-alert", inputs: { type: "type", svgIcon: "svgIcon", size: "size", testId: "testId", hasCloseButton: "hasCloseButton", isBanner: "isBanner", ariaLabelCloseButton: "ariaLabelCloseButton" }, outputs: { closed: "closed" }, host: { properties: { "attr.role": "this.role" } }, ngImport: i0, template: "<div\r\n  class=\"centric-alert {{ type }}\"\r\n  [class.is-banner]=\"isBanner\"\r\n  [class.alert-with-close]=\"hasCloseButton\"\r\n  [attr.data-testId]=\"testId\">\r\n  <centric-icon2\r\n    class=\"status-icon\"\r\n    [svgIcon]=\"svgIcon ? svgIcon : icons[type]\"\r\n    [size]=\"size ? size : '20'\"></centric-icon2>\r\n  <div class=\"message-content\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n  @if (hasCloseButton) {\r\n    <div class=\"right-container\">\r\n      <button\r\n        class=\"close-button\"\r\n        [attr.aria-label]=\"ariaLabelCloseButton\"\r\n        (click)=\"closeButtonClick()\">\r\n        <centric-icon2\r\n          svgIcon=\"cancel_b\"\r\n          size=\"20\">\r\n        </centric-icon2>\r\n      </button>\r\n    </div>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-alert', imports: [CommonModule, CentricIcon2Component], standalone: true, template: "<div\r\n  class=\"centric-alert {{ type }}\"\r\n  [class.is-banner]=\"isBanner\"\r\n  [class.alert-with-close]=\"hasCloseButton\"\r\n  [attr.data-testId]=\"testId\">\r\n  <centric-icon2\r\n    class=\"status-icon\"\r\n    [svgIcon]=\"svgIcon ? svgIcon : icons[type]\"\r\n    [size]=\"size ? size : '20'\"></centric-icon2>\r\n  <div class=\"message-content\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n  @if (hasCloseButton) {\r\n    <div class=\"right-container\">\r\n      <button\r\n        class=\"close-button\"\r\n        [attr.aria-label]=\"ariaLabelCloseButton\"\r\n        (click)=\"closeButtonClick()\">\r\n        <centric-icon2\r\n          svgIcon=\"cancel_b\"\r\n          size=\"20\">\r\n        </centric-icon2>\r\n      </button>\r\n    </div>\r\n  }\r\n</div>\r\n" }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], type: [{
                type: Input
            }], svgIcon: [{
                type: Input
            }], size: [{
                type: Input
            }], testId: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], isBanner: [{
                type: Input
            }], ariaLabelCloseButton: [{
                type: Input
            }], closed: [{
                type: Output
            }] } });

const moduleComponents = [AlertComponent];
class CentricAlertModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricAlertModule, imports: [CommonModule, CentricIcon2Module, AlertComponent], exports: [AlertComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricAlertModule, imports: [CommonModule, CentricIcon2Module, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AlertComponent, CentricAlertModule };
